import { useState } from 'react';
import { useAppDispatch, useAppSelector } from 'src/hooks';
import {
  currentAttendeeEventsSidebarSelector,
  showAttendeeEventsSidebarSelector,
} from 'src/redesign/features/AttendeeEventsSidebar/store/selectors';
import { Sidebar, SidebarPassThroughOptions } from 'primereact/sidebar';
import { attendeeEventsSidebarActions } from 'src/redesign/features/AttendeeEventsSidebar/store/reducer';
import S from './AttendeeEventsSidebar.module.scss';
import { Text } from '@components/Text';
import calendarIcon from 'src/redesign/assets/calendar-icon.svg';
import timeIcon from 'src/redesign/assets/time-icon.svg';
import locationIcon from 'src/redesign/assets/location-icon.svg';
import { RedesignButton } from '@components/RedesignButton';
import { DownloadIcon, ExternalLinkIcon, EyeIcon, TicketIcon } from 'src/redesign/assets/icons';
import { Tag } from 'primereact/tag';
import c from 'clsx';
import styles from 'src/redesign/features/AttendeesSidebar/views/OrderSidebarView/components/OrderCard/OrderCard.module.scss';
import { Divider } from 'primereact/divider';
import { getPaymentLogo } from 'src/redesign/shared/utils/getPaymentLogo';
import {
  fetchTicketPdf,
  fetchTicketReceiptPdf,
} from 'src/redesign/features/Attendees/store/actions';
import moment from 'moment';
import { Dialog } from 'primereact/dialog';
import { Carousel } from 'primereact/carousel';
import appleStoreIcon from 'src/redesign/assets/apple-store-icon.svg';
import playStoreIcon from 'src/redesign/assets/play-store-icon.svg';
import { Button } from 'primereact/button';

const pt: SidebarPassThroughOptions = {
  root: {
    className: S.attendeeEventsSidebar,
  },
  header: {
    className: S.sidebarHeader,
  },
  content: {
    style: {
      padding: 0,
    },
  },
};

export const AttendeeEventsSidebar = () => {
  const [showQrCodeDialog, setShowQrCodeDialog] = useState(false);
  const showSidebar = useAppSelector(showAttendeeEventsSidebarSelector);
  const ticketInfo = useAppSelector(currentAttendeeEventsSidebarSelector);
  const dispatch = useAppDispatch();
  const actions = attendeeEventsSidebarActions;

  if (!ticketInfo) return null;

  const {
    event,
    cart_id,
    friendly_cart_id,
    purchase_date,
    total,
    refund_amount,
    tickets,
    payment_method,
    card_type,
    last4,
  } = ticketInfo;

  // Format purchase date
  const purchaseDate = moment(purchase_date).format('dddd, D MMMM YYYY h:mm A');

  // Format event date and time from start_date and end_date
  const startDate = moment(event.start_date);
  const endDate = moment(event.end_date);

  // Format date part
  let displayDate = '';

  // If start and end dates are on the same day
  if (startDate.format('YYYY-MM-DD') === endDate.format('YYYY-MM-DD')) {
    displayDate = startDate.format('D MMM YYYY');
  }
  // If start and end dates are in the same month and year
  else if (startDate.format('MMM YYYY') === endDate.format('MMM YYYY')) {
    displayDate = `${startDate.format('D')} - ${endDate.format('D MMM YYYY')}`;
  }
  // If start and end dates are in different months but same year
  else if (startDate.format('YYYY') === endDate.format('YYYY')) {
    displayDate = `${startDate.format('D MMM')} - ${endDate.format('D MMM YYYY')}`;
  }
  // If start and end dates are in different years
  else {
    displayDate = `${startDate.format('D MMM YYYY')} - ${endDate.format('D MMM YYYY')}`;
  }

  // Format time part
  const startTime = startDate.format('h:mm A');
  const endTime = endDate.format('h:mm A');
  const displayTime = `${startTime} - ${endTime}`;

  // Get payment logo
  const paymentLogoComponent = getPaymentLogo(card_type?.toLowerCase() || '');

  // Order status tag class
  const hasRefunds = refund_amount > 0;
  const classNames = c(styles.badge, {
    [styles.refunded]: hasRefunds,
    [styles.success]: !hasRefunds,
  });

  // QR Code carousel template
  const qrCodeTemplate = (ticket) => {
    return (
      <div className={S.qrCodeCard}>
        <div className={S.attendeeBadge}>
          <Text>
            {ticket.first_name} {ticket.last_name}
          </Text>
        </div>

        <img src={ticket.qrcode} alt="Ticket QR Code" className={S.qrCodeImage} />

        <div className={S.walletButtons}>
          {ticket.google_wallet_link && (
            <a
              href={ticket.google_wallet_link}
              target="_blank"
              rel="noopener noreferrer"
              className={S.walletButton}
            >
              <img src={playStoreIcon} alt="Google Wallet" />
              <Text color="black-400">Add to Google Wallet</Text>
            </a>
          )}

          {ticket.apple_wallet_link && (
            <a
              href={ticket.apple_wallet_link}
              target="_blank"
              rel="noopener noreferrer"
              className={S.walletButton}
            >
              <img src={appleStoreIcon} alt="Apple Wallet" />
              <Text color="black-400">Add to Apple Wallet</Text>
            </a>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      <Dialog
        header="View QR Code"
        visible={showQrCodeDialog}
        onHide={() => setShowQrCodeDialog(false)}
        className={S.qrCodeDialog}
        style={{ width: '90%', maxWidth: '400px' }}
        draggable={false}
        resizable={false}
        closeOnEscape
      >
        {tickets.length > 0 &&
          (tickets.length === 1 ? (
            qrCodeTemplate(tickets[0])
          ) : (
            <Carousel
              value={tickets}
              numVisible={1}
              numScroll={1}
              itemTemplate={qrCodeTemplate}
              showIndicators
              showNavigators={true}
              circular
            />
          ))}
      </Dialog>

      <Sidebar
        pt={pt}
        visible={showSidebar}
        header="Event Details"
        position="right"
        onHide={() => dispatch(actions.hideAttendeeEventsSidebar())}
      >
        <div className={S.attendeeEventContent}>
          <div className={S.ticket}>
            <div className={S.titleContainer}>
              <img
                src={event.image_url || 'https://cdn.ticketspot.io/no-image-default.png'}
                alt="event"
                className={S.eventIcon}
              />
              <Text variation="h5" bold>
                {event.title}
              </Text>
            </div>
            <div className={S.informationBlock}>
              <div className={S.dateInfo}>
                <img src={calendarIcon} alt="" />
                <Text>{displayDate}</Text>
              </div>
              <div className={S.timeInfo}>
                <img src={timeIcon} alt="" />
                <Text>{displayTime}</Text>
              </div>
              {event.location && (
                <div className={S.positionInfo}>
                  <img src={locationIcon} alt="" /> {event.location}
                </div>
              )}
            </div>
            <Button
              text
              onClick={() => window.open(`/#/events/manage/${event.id}`, '_blank')}
              className={styles.eventDetailsButton}
            >
              <Text hoverColor="#9D25DCFF" color="brand-400" regular>
                View Event Details
              </Text>
              <ExternalLinkIcon />
            </Button>
          </div>
          <div className={S.ticketInfoWrapper}>
            <div className={S.infoRow}>
              <div className={S.rowTitle}>
                <Text variation="body-small" color="black-100">
                  Order ID
                </Text>
                <Text variation="h5">#{friendly_cart_id}</Text>
              </div>
              <Tag
                className={classNames}
                severity={hasRefunds ? 'warning' : 'success'}
                value={hasRefunds ? 'Partially Refunded' : 'Successful'}
              />
            </div>
            <div className={S.infoRow}>
              <div className={S.rowTitle}>
                <Text variation="body-small" color="black-100">
                  Purchased Date
                </Text>
                <Text variation="h5">{purchaseDate}</Text>
              </div>
            </div>
            <div className={S.infoRow}>
              <div className={S.rowTitle}>
                <Text variation="body-small" color="black-100">
                  Tickets Purchased
                </Text>
                <Text variation="h5">
                  {tickets.length} ticket{tickets.length !== 1 ? 's' : ''}
                </Text>
              </div>
              <Divider layout="vertical" className={S.divider} />
              <div className={S.rowTitle}>
                <Text variation="body-small" color="black-100">
                  Total
                </Text>
                <Text variation="h5">
                  {ticketInfo.currency_symbol}
                  {total.toFixed(2)}
                </Text>
              </div>
              <Divider layout="vertical" className={S.divider} />
              <div className={S.rowTitle}>
                <Text variation="body-small" color="black-100">
                  Refunds
                </Text>
                <Text variation="h5">
                  {ticketInfo.currency_symbol}
                  {refund_amount.toFixed(2)}
                </Text>
              </div>
            </div>
            {payment_method !== 'N/A' && (
              <div className={S.infoRow}>
                <div className={S.rowTitle}>
                  <Text variation="body-small" color="black-100">
                    Payment Details
                  </Text>
                  <div className={S.paymentCardWrapper}>
                    {paymentLogoComponent && (
                      <div className={S.paymentLogo}>{paymentLogoComponent}</div>
                    )}
                    {card_type !== 'N/A'
                      ? `${card_type} (${last4 ? `** ${last4}` : ''})`
                      : payment_method}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className={S.buttons}>
          {tickets.some((ticket) => ticket.qrcode) && (
            <RedesignButton
              variation="primary"
              height={52}
              className={S.firstButton}
              onClick={() => setShowQrCodeDialog(true)}
            >
              <TicketIcon />
              <Text regular color="white-100">
                View QR Code
              </Text>
            </RedesignButton>
          )}
          <RedesignButton
            variation="secondary"
            height={52}
            onClick={() => dispatch(fetchTicketReceiptPdf(cart_id))}
          >
            <EyeIcon />
            <Text regular color="brand-400">
              View Receipt
            </Text>
          </RedesignButton>
          <RedesignButton
            variation="secondary"
            height={52}
            onClick={() => dispatch(fetchTicketPdf(cart_id))}
          >
            <DownloadIcon />
            <Text regular color="brand-400">
              Download PDF
            </Text>
          </RedesignButton>
        </div>
      </Sidebar>
    </>
  );
};
